import cn from 'classnames';
import * as React from 'react';
import { Fragment, useCallback, useRef, useState } from 'react';
import useSWR from 'swr';

import { textVariableReplacer } from '@/helpers/common';
import usePublicTranslation from '@/hooks/usePublicTranslation';

import styles from './styles.module.scss';
import { Certificate } from './types';

const CERTIFICATES_URL = `/data/structure/entity/view/task/public/{dataStructureId}/{viewId}/certifications`;

type Props = {
  dataStructureId: string;
  viewId: string;
  className?: string;
};

const TrustCenter: React.FC<Props> = ({ dataStructureId, viewId, className }) => {
  const t = usePublicTranslation();
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  const { data: certificates } = useSWR<Certificate[]>(
    searchTerm
      ? textVariableReplacer(CERTIFICATES_URL, {
          dataStructureId,
          viewId
        }) + `?search=${searchTerm}`
      : null
  );

  const handleSearch = useCallback(() => {
    if (!searchRef.current || searchRef.current.value.length === 0) {
      return;
    }

    setSearchTerm(searchRef.current.value);
  }, []);

  const handleSearchSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSearch();
    },
    [handleSearch]
  );

  return (
    <div className={cn(styles.block, className)}>
      <div className={cn(styles.formContainer, 'formContainer')}>
        <form className={cn(styles.form, 'form')} onSubmit={handleSearchSubmit}>
          <input
            ref={searchRef}
            type="text"
            name="search"
            defaultValue={searchTerm}
            className={cn(styles.input, 'input')}
            placeholder={t('certificates.search_placeholder')}
          />
          <button type="submit" className={cn(styles.button, 'button')} onClick={handleSearch}>
            <i className="fa-light fa-magnifying-glass"></i>
            {t('certificates.search_button')}
          </button>
        </form>
      </div>
      {searchTerm && (
        <Fragment>
          <div className={cn(styles.header, 'header')}>
            <div className={cn(styles.title, 'title')}>
              {t('certificates.search_results_for', { query: searchTerm })}:
            </div>
            <div className={cn(styles.results, 'results')}>
              {certificates?.length == 1
                ? t('certificates.one_result_found')
                : t('certificates.results_found', { count: certificates?.length?.toString() ?? '0' })}
            </div>
          </div>
          <div className={cn(styles.tableContainer, 'tableContainer')}>
            <table className={cn(styles.table, 'table')}>
              <thead>
                <tr className={cn(styles.headerRow, 'headerRow')}>
                  <th className={cn(styles.th, 'th')}>{t('certificates.organisation_name')}</th>
                  <th className={cn(styles.th, 'th')}>{t('certificates.certificate_reference')}</th>
                  <th className={cn(styles.th, 'th')}>{t('certificates.certificate_level')}</th>
                  {/*<th className={cn(styles.th, 'th')}>{t('certificates.issue_date')}</th>*/}
                  <th className={cn(styles.th, 'th')}>{t('certificates.status')}</th>
                </tr>
              </thead>
              <tbody>
                {certificates?.map((cert, index) => (
                  <tr key={index} className={cn(styles.row, 'row')}>
                    <td className={cn(styles.td, 'td')}>{cert.organisationName}</td>
                    <td className={cn(styles.td, 'td')}>{cert.certificateReference}</td>
                    <td className={cn(styles.td, 'td')}>{cert.certificateLevel}</td>
                    {/*<td className={cn(styles.td, 'td')}>*/}
                    {/*  {cert.issueDate ? moment(cert.issueDate * 1000).format('DD.MM.YYYY') : t('general.na')}*/}
                    {/*</td>*/}
                    <td className={cn(styles.td, 'td')}>{cert.organisationStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default TrustCenter;
